/**
 * Lightweight primer design + integrity checks.
 *
 * For new primer design from scratch, Primer3 (WASM port) is the v0.3
 * tool; this module supplies:
 *   - Tm estimation (Wallace + nearest-neighbour-light variants)
 *   - Primer-on-reference search (with mismatches)
 *   - Drift-detection: given a known primer + new variant fingerprint,
 *     report whether the primer site is impacted
 */

import { reverseComplement } from './fasta.js';

/**
 * Wallace rule (Tm = 2(A+T) + 4(G+C)) — simple, fast, OK for primers
 * 14-20nt. Use nearestNeighbourTm for larger primers.
 */
export function wallaceTm(primer: string): number {
  let at = 0, gc = 0;
  for (const c of primer.toUpperCase()) {
    if (c === 'A' || c === 'T') at++;
    else if (c === 'G' || c === 'C') gc++;
  }
  return 2 * at + 4 * gc;
}

/**
 * Find primer binding sites on a reference (forward + reverse-
 * complement). Allows up to `maxMismatches` (Hamming).
 */
export interface PrimerMatch {
  position: number; // 0-based start
  strand: '+' | '-';
  mismatches: number;
}

export function findPrimerMatches(
  primer: string, reference: string, maxMismatches = 0,
): PrimerMatch[] {
  const out: PrimerMatch[] = [];
  const ref = reference.toUpperCase();
  const p = primer.toUpperCase();
  const rcP = reverseComplement(p);
  const pLen = p.length;
  for (let i = 0; i + pLen <= ref.length; i++) {
    let mm = 0;
    for (let j = 0; j < pLen; j++) {
      if (ref[i + j] !== p[j]) { mm++; if (mm > maxMismatches) break; }
    }
    if (mm <= maxMismatches) out.push({ position: i, strand: '+', mismatches: mm });
    let mm2 = 0;
    for (let j = 0; j < pLen; j++) {
      if (ref[i + j] !== rcP[j]) { mm2++; if (mm2 > maxMismatches) break; }
    }
    if (mm2 <= maxMismatches) out.push({ position: i, strand: '-', mismatches: mm2 });
  }
  return out;
}
